import React, { useState, useEffect } from 'react';
import Media from 'react-media';
import styled from 'styled-components';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(n);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var _templateObject, _templateObject2;
/**
 * Toggles which of 2 components are displayed for a specified CSS media query.
 *
 * Must wrap exactly 2 React child components.
 * The first child component will be displayed when the media query evaluates to false.
 * The second child compnent will be displayed when the media query evaluates to true.
 *
 * If the wrapped components are server side rendered,
 * we will also need to do a 2 stage render in order
 * for the hydrate process to work correctly
 * see: https://reactjs.org/docs/react-dom.html#hydrate
 *
 * 2 stage renders are potentially expensive and may have
 * unwanted side effects because both components are actually
 * mounted in the first render. Thus, 2 stage renders are
 * NOT performed by default. It is up to the user of this
 * class to pass in the isSSR prop in order to trigger the
 * 2 stage render behavior.
 *
 * Another alternative for SSR is to just not render the
 * DisplayToggle until the calling component has mounted.
 * In this case, options are to just render out null and
 * wait for the React JS to download, parse, and hydrate
 * or render out a placeholder/shell component while waiting.
 */


var DisplayToggle = function DisplayToggle(_ref) {
  var query = _ref.query,
      children = _ref.children,
      isSSR = _ref.isSSR;

  var _useState = useState(!isSSR),
      _useState2 = _slicedToArray(_useState, 2),
      isClient = _useState2[0],
      setIsClient = _useState2[1];

  useEffect(function () {
    return setIsClient(true);
  }, []);
  var childArray = React.Children.toArray(children); // Client side render.
  // This uses window.matchMedia under the hood.

  if (isClient) {
    return (
      /*#__PURE__*/
      React.createElement(Media, {
        query: query
      }, function (matches) {
        return matches ? childArray[1] : childArray[0];
      })
    );
  } // SSR render.
  // Will be the initial/first client side render when isSSR prop is passed.
  // Once the app hydrates, the isClient will get set to true and we will
  // use the more efficient client version for all subsequent renders.


  return (
    /*#__PURE__*/
    React.createElement(React.Fragment, null,
    /*#__PURE__*/
    React.createElement(DisplayWhenQueryIsFalse, {
      query: query
    }, childArray[0]),
    /*#__PURE__*/
    React.createElement(DisplayWhenQueryIsTrue, {
      query: query
    }, childArray[1]))
  );
}; // Server side renders wrap each component in a div
// and combine media query with CSS display property
// to show exactly one component and hide the other.


var DisplayWhenQueryIsFalse = styled.div(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n  @media screen and ", " {\n    display: none;\n  }\n"])), function (props) {
  return props.query;
});
DisplayWhenQueryIsFalse.displayName = 'DisplayWhenQueryIsFalse';
var DisplayWhenQueryIsTrue = styled.div(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n  @media not screen and ", " {\n    display: none;\n  }\n"])), function (props) {
  return props.query;
});
DisplayWhenQueryIsTrue.displayName = 'DisplayWhenQueryIsTrue';

var _templateObject$1;
/**
 * Toggles from Flex column to Flex row when query evaluates to true.
 */


var FlexColumnToRowToggle = function FlexColumnToRowToggle(_ref) {
  var children = _ref.children,
      className = _ref.className,
      query = _ref.query;
  return (
    /*#__PURE__*/
    React.createElement(Flex, {
      className: className,
      query: query
    }, children)
  );
};

var Flex = styled.div(_templateObject$1 || (_templateObject$1 = _taggedTemplateLiteral(["\n  display: flex;\n  flex-direction: column;\n\n  @media screen and ", " {\n    flex-direction: row;\n  }\n"])), function (props) {
  return props.query;
});
Flex.displayName = 'Flex';

var _templateObject$2;
/**
 * Children will be visible when query is true.
 * Children will be hidden when query is false.
 *
 * If the wrapped component is server side rendered,
 * we will also need to do a 2 stage render in order
 * for the hydrate process to work correctly
 * see: https://reactjs.org/docs/react-dom.html#hydrate
 *
 * 2 stage renders are potentially expensive and may have
 * unwanted side effects because the component is actually
 * mounted in the first render. Thus, 2 stage renders are
 * NOT performed by default. It is up to the user of this
 * class to pass in the isSSR prop in order to trigger the
 * SSR friendly 2 stage render behavior.
 */


var MediaQuery = function MediaQuery(_ref) {
  var query = _ref.query,
      children = _ref.children,
      isSSR = _ref.isSSR;

  var _useState = useState(!isSSR),
      _useState2 = _slicedToArray(_useState, 2),
      isClient = _useState2[0],
      setIsClient = _useState2[1];

  useEffect(function () {
    return setIsClient(true);
  }, []); // Client side render.
  // This uses window.matchMedia under the hood.

  if (isClient) {
    return (
      /*#__PURE__*/
      React.createElement(Media, {
        query: query
      }, function (matches) {
        return matches ? children : null;
      })
    );
  } // SSR render.
  // Will be the initial/first client side render when isSSR prop is passed.
  // Once the app hydrates, the isClient will get set to true and we will
  // use the more efficient client version for all subsequent renders.


  return (
    /*#__PURE__*/
    React.createElement(MediaQueryWrapper, {
      query: query
    }, children)
  );
}; // Server side renders wrap the component in a div
// and combine media query with CSS display property
// to show/hide the component.


var MediaQueryWrapper = styled.div(_templateObject$2 || (_templateObject$2 = _taggedTemplateLiteral(["\n  @media not screen and ", " {\n    display: none;\n  }\n"])), function (props) {
  return props.query;
});
MediaQueryWrapper.displayName = 'MediaQuery';
/**
 * Breakpoints and media queries
 * Following this article #3
 * https://www.freecodecamp.org/news/the-100-correct-way-to-do-css-breakpoints-88d6a5ba1862/
 *
 * usage:
 * const StyledComponent = styled(Element)`
     [...]
     ${MQ_FOR_TABLET_PORTRAIT_UP} {
       [...]
     }
   `;
 */

var QUERY_FOR_PHONE_ONLY = "(max-width: 599px) and (orientation: portrait), (max-width: 900px) and (orientation: landscape)";
var MQ_FOR_PHONE_ONLY = "@media ".concat(QUERY_FOR_PHONE_ONLY);
var QUERY_FOR_PHONE_WIDTH = "(max-width: 599px)";
var MQ_FOR_PHONE_WIDTH = "@media ".concat(QUERY_FOR_PHONE_WIDTH);
var QUERY_FOR_PHONE_PORTRAIT = "(max-width: 599px) and (orientation: portrait)";
var MQ_FOR_PHONE_PORTRAIT = "@media ".concat(QUERY_FOR_PHONE_PORTRAIT);
var QUERY_FOR_PHONE_LANDSCAPE = "(max-width: 900px) and (orientation: landscape)";
var MQ_FOR_PHONE_LANDSCAPE = "@media ".concat(QUERY_FOR_PHONE_LANDSCAPE);
var QUERY_FOR_TABLET_UP = "(min-width: 600px) and (orientation: portrait), (min-width: 900px) and (orientation: landscape)";
var MQ_FOR_TABLET_UP = "@media ".concat(QUERY_FOR_TABLET_UP);
var QUERY_FOR_TABLET_DOWN = "(max-width: 900px) and (orientation: portrait), (max-width: 1024px) and (orientation: landscape)";
var MQ_FOR_TABLET_DOWN = "@media ".concat(QUERY_FOR_TABLET_DOWN);
var QUERY_FOR_TABLET_PORTRAIT_UP = "(min-width: 600px) and (min-height: 600px)";
var MQ_FOR_TABLET_PORTRAIT_UP = "@media ".concat(QUERY_FOR_TABLET_PORTRAIT_UP);
var QUERY_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT = "(min-width: 600px)";
var MQ_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT = "@media ".concat(QUERY_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT);
var QUERY_FOR_SMALL_TABLET_ONLY = "(min-width: 600px) and (max-width: 899px)";
var MQ_FOR_SMALL_TABLET_ONLY = "@media ".concat(QUERY_FOR_SMALL_TABLET_ONLY);
var QUERY_FOR_TABLET_PORTRAIT_ONLY = "(min-width: 600px) and (orientation: portrait), (max-width: 1024) and (orientation: portait)";
var MQ_FOR_TABLET_PORTRAIT_ONLY = "@media ".concat(QUERY_FOR_TABLET_PORTRAIT_ONLY);
var QUERY_FOR_TABLET_PORTRAIT_DOWN = "(max-width: 1024px) and (orientation: portrait)";
var MQ_FOR_TABLET_PORTRAIT_DOWN = "@media ".concat(QUERY_FOR_TABLET_PORTRAIT_DOWN);
var QUERY_FOR_TABLET_LANDSCAPE_UP = "(min-width: 900px)";
var MQ_FOR_TABLET_LANDSCAPE_UP = "@media ".concat(QUERY_FOR_TABLET_LANDSCAPE_UP);
var QUERY_FOR_TABLET_LANDSCAPE_BELOW = "(max-width: 899px)";
var MQ_FOR_TABLET_LANDSCAPE_BELOW = "@media ".concat(QUERY_FOR_TABLET_LANDSCAPE_BELOW);
var QUERY_FOR_DESKTOP_UP = "(min-width: 1200px)";
var MQ_FOR_DESKTOP_UP = "@media ".concat(QUERY_FOR_DESKTOP_UP);
var QUERY_FOR_DESKTOP_BELOW = "(max-width: 1199px)";
var MQ_FOR_DESKTOP_BELOW = "@media ".concat(QUERY_FOR_DESKTOP_BELOW);
var QUERY_FOR_LARGE_DESKTOP_UP = "(min-width: 1600px)";
var MQ_FOR_LARGE_DESKTOP_UP = "@media ".concat(QUERY_FOR_LARGE_DESKTOP_UP);
var QUERY_FOR_BIG_DESKTOP_UP = "(min-width: 1800px)";
var MQ_FOR_BIG_DESKTOP_UP = "@media ".concat(QUERY_FOR_BIG_DESKTOP_UP);
/* Note: Following Media Queries Added to Match the NavigationBar Swap to Mobile Top Bar as of 11/19 */

var QUERY_FOR_MOBILE_UP = "(min-width: 768px)";
var MQ_FOR_MOBILE_UP = "@media ".concat(QUERY_FOR_MOBILE_UP);
var QUERY_FOR_MOBILE_BELOW = "(max-width: 767px)";
var MQ_FOR_MOBILE_BELOW = "@media ".concat(QUERY_FOR_MOBILE_BELOW);
/* Note: used to force space selector to show on Ipad landscape as of 12/18/2019 */

var QUERY_FOR_TABLET_LANDSCAPE_ONLY = "(min-width: 1024px) and (orientation: landscape), (max-width: 1366px) and (orientation: landscape)";
export { DisplayToggle, FlexColumnToRowToggle, MQ_FOR_BIG_DESKTOP_UP, MQ_FOR_DESKTOP_BELOW, MQ_FOR_DESKTOP_UP, MQ_FOR_LARGE_DESKTOP_UP, MQ_FOR_MOBILE_BELOW, MQ_FOR_MOBILE_UP, MQ_FOR_PHONE_LANDSCAPE, MQ_FOR_PHONE_ONLY, MQ_FOR_PHONE_PORTRAIT, MQ_FOR_PHONE_WIDTH, MQ_FOR_SMALL_TABLET_ONLY, MQ_FOR_TABLET_DOWN, MQ_FOR_TABLET_LANDSCAPE_BELOW, MQ_FOR_TABLET_LANDSCAPE_UP, MQ_FOR_TABLET_PORTRAIT_DOWN, MQ_FOR_TABLET_PORTRAIT_ONLY, MQ_FOR_TABLET_PORTRAIT_UP, MQ_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT, MQ_FOR_TABLET_UP, MediaQuery, QUERY_FOR_BIG_DESKTOP_UP, QUERY_FOR_DESKTOP_BELOW, QUERY_FOR_DESKTOP_UP, QUERY_FOR_LARGE_DESKTOP_UP, QUERY_FOR_MOBILE_BELOW, QUERY_FOR_MOBILE_UP, QUERY_FOR_PHONE_LANDSCAPE, QUERY_FOR_PHONE_ONLY, QUERY_FOR_PHONE_PORTRAIT, QUERY_FOR_PHONE_WIDTH, QUERY_FOR_SMALL_TABLET_ONLY, QUERY_FOR_TABLET_DOWN, QUERY_FOR_TABLET_LANDSCAPE_BELOW, QUERY_FOR_TABLET_LANDSCAPE_ONLY, QUERY_FOR_TABLET_LANDSCAPE_UP, QUERY_FOR_TABLET_PORTRAIT_DOWN, QUERY_FOR_TABLET_PORTRAIT_ONLY, QUERY_FOR_TABLET_PORTRAIT_UP, QUERY_FOR_TABLET_PORTRAIT_UP_NO_MIN_HEIGHT, QUERY_FOR_TABLET_UP };